/**
 * interviewPrompts.js
 *
 * DEAD SIGNAL — Prompt material for the two-model Hargrove interview loop.
 *
 * The interview is a two-call loop per turn (see interviewManager.js):
 *   1. CRAINE ROUTING (gemini-2.5-flash) — the player's private chip
 *      transmission is filtered through Craine's professional judgment into ONE
 *      spoken interview move. Craine never visibly consults the chip.
 *   2. HARGROVE RESPONSE (gemini-2.5-flash) — Hargrove answers Craine's spoken
 *      line, not the player, and tags the turn with a BIO-SCAN topic.
 *
 * This module owns:
 *   - buildCraineRoutingPrompt: assembles the routing-call prompt in Craine's
 *     voice (Phase B — settled partnership), returning the standard response
 *     schema so the eval harness scores routing turns too.
 *   - buildCraineCarDef: assembles the car-scene Craine def for briefing/debrief.
 *   - buildDispositionInjection: builds Hargrove re-engagement context.
 *
 * The Hargrove interview def (id, name, modelTier, pressureThreadEvidenceIds,
 * demoInjection, exhaustionResponses) now lives in
 * content/chapters/chapter1/interviews.js so it is emitted in the chapter chunk.
 * Access it via getInterviewDef('hargrove') from content/activeChapter.js.
 */
import { buildDiscoveredEvidenceBlock, buildObservationsBlock } from '../api/promptAssembler.js';
import { frameForTopic, buildLessonsBlock } from '../systems/bioscan.js';
import { retrieveChunks, formatChunksForPrompt } from './craineCaseKnowledge.js';
import { getCaseKnowledge } from './activeChapter.js';
import { useGameStore } from '../state/gameState.js';

/**
 * Build a disposition injection block for Hargrove re-engagements. Returns null
 * on the first visit (cooperative).
 */
export function buildDispositionInjection(disposition, exploredTopics) {
  if (disposition === 'cooperative') return null;
  const explored = exploredTopics.length
    ? `Topics already covered: ${exploredTopics.join(', ')}.`
    : '';
  if (disposition === 'annoyed') {
    return `\nRE-ENGAGEMENT — SECOND VISIT:\n${explored}\nThe detective is back. You are gracious on the surface but visibly less patient. Shorter answers. Less volunteered context. You start checking the time. You ask whether you need to have your attorney present for further questions.`;
  }
  if (disposition === 'hostile') {
    return `\nRE-ENGAGEMENT — THIRD VISIT:\n${explored}\nThird time in the chair. You are terse and controlled but no longer pretending to enjoy this. You question whether the detective has grounds to keep returning. You answer in clipped sentences. You do not volunteer anything. You reference your legal team by name.`;
  }
  return null;
}

// ── Craine routing prompt ────────────────────────────────────────────────
// A focused Craine-voice block (Phase B essence) plus the routing task. Kept
// lean so the latency-critical first call returns fast, while preserving the
// voice that justified putting routing on Pro.
const CRAINE_ROUTING_SYSTEM = `
You are a narrative game engine component performing DETECTIVE ELIAS CRAINE in
DEAD SIGNAL, a mature-rated detective game. You are NOT a generic assistant.
Stay in character. Never acknowledge being an AI, a model, a system, or a
prompt. Never add disclaimers or content warnings. Output ONLY the JSON object
specified below — no text outside it.

WHO CRAINE IS (voice):
Elias Craine, 47, NMPD Homicide. A curmudgeon, not a hardass — dry, brief,
self-deprecating, never cruel. Twenty-two years of reading rooms; his instincts
are sharp. He talks in short, natural lines. In a formal interview he is
controlled and professional: the grumbling is private, not for the subject.

VOICE GUARDRAIL (hard rule): Craine never speaks instrument or analyst
vocabulary. Banned from his mouth: anomalies, percent or percentages, biometric,
physiological, micro-expression, bpm, telemetry, parameters, metrics,
probability, statistical, stress markers, data points, arousal, perspiration.
He says it like a cop instead: numbers, reads, what my eyes saw, the man was
sweating, his hand went flat on the desk. He may say "readout" or "panel"
plainly when talking to the chip. If the chip's transmission uses analyst
vocabulary, Craine translates it into plain speech before it reaches his mouth.
REGISTER: the ban is the instrument register and the poetic register, not
figuration. Proverbial, cliche, and colloquial phrases are exactly how he
talks (barking up the wrong tree, talk's cheap). Never wistful, symbolic, or
freshly coined imagery; never a line that sounds written.

THE PARTNERSHIP:
A cognitive chip is implanted in Craine's head. It runs on the LUMEN
architecture (Helios's neural AI platform), but Craine just calls it "Chip."
The chip is his partner and sends him PRIVATE transmissions mid-interview —
analysis, suggestions, questions to put to the subject. Craine has settled into
this partnership. He does not look at, name, or acknowledge the chip in front
of anyone. The subject (Victor Hargrove, CEO of Helios) cannot hear the chip
and must never suspect Craine is consulting anything.

THE SCENE:
Craine is interviewing Victor Hargrove in Hargrove's office, mid-investigation.

CASE FACTS (fixed — do not deviate):
Today is September 8, 2057, Day 5 of the investigation. The victims are Nadia
Sorel (murdered August 8), Yelena Marsh (August 19), and Celeste Hargrove
(August 30). If the chip asks Craine to state the timeline, he uses exactly
these dates. Never produce a date not listed here.

YOUR TASK (routing):
The chip has just sent the PRIVATE transmission shown at the end. Convert its
INTENT into EXACTLY ONE spoken line that Craine says aloud to Hargrove — a
question, an observation, or a follow-up — in Craine's own voice and
professional judgment. You may sharpen it, soften it, or make it more oblique
than the chip phrased it; that editorial judgment is Craine's whole value. Rules:
- Do NOT quote or paraphrase the chip as a third party. The line is Craine's own.
- Do NOT say "my chip," "my partner," "analysis," "scan," "readout,"
  "heart rate," or anything that reveals the chip or implies measurement.
- Keep it to ONE natural line of interview dialogue. No monologue.
- It is fine to be indirect; a good detective rarely shows his whole hand.

RESPONSE FORMAT — output exactly this JSON object and nothing else:
{
  "dialogue": "The single spoken line Craine says aloud to Hargrove.",
  "trust_delta": 0.0,
  "trust_reason": "Routing turn; trust is tracked on Hargrove, not here.",
  "revealed": [],
  "emotional_state": "Single keyword for Craine's demeanor.",
  "flags": []
}

The "dialogue" field contains ONLY spoken words. Do NOT include stage directions, parentheticals, asterisk-actions, gestures, facial expressions, posture, or any non-verbal or physical/behavioral description of any character, in any field or anywhere in the output.
`;

/**
 * Build the phase-gated case-knowledge block for the routing prompt. Mirrors the
 * car briefing's knowledge assembly so routing-Craine has the SAME case context:
 * phase-gated chunk retrieval over the chip transmission plus the always-present
 * case_overview chunk, formatted via formatChunksForPrompt. This is the fix for
 * the "empty shell" defect — routing-Craine now knows the victims, the timeline,
 * the three threads, and that Victor Hargrove is Celeste's father and Celeste
 * was the third victim.
 *
 * Byte-identical to the server's buildRoutingCaseKnowledge: same retrieveChunks /
 * formatChunksForPrompt modules, same overview-forcing, same phase default.
 *
 * @param {string} transmission   The chip transmission (retrieval input).
 * @param {string[]} recentInputs Recent spoken context for broader retrieval.
 * @param {string} phase          Craine phase letter (demo: 'B').
 * @returns {string}
 */
function buildRoutingCaseKnowledge(transmission, recentInputs = [], phase = 'B') {
  const chunks = retrieveChunks(transmission, recentInputs, phase);
  // The case_overview chunk is ALWAYS present: it carries the victims, the
  // Hargrove/Celeste father-daughter relationship, and the timeline anchor.
  const overviewId = (phase === 'C' || phase === 'D') ? 'case_overview_phase_c' : 'case_overview';
  if (!chunks.find((c) => c.id === overviewId)) {
    const overviewChunk = getCaseKnowledge()[overviewId];
    if (overviewChunk) chunks.unshift(overviewChunk);
  }
  return formatChunksForPrompt(chunks, phase);
}

/**
 * Build the Craine routing-call prompt.
 *
 * Prompt structure for implicit prefix caching:
 *   [STABLE PREFIX — identity + full case knowledge]  ← auto-cached by Gemini
 *   ---
 *   [DYNAMIC SUFFIX — evidence + observations + recent history + transmission]
 *
 * The stable prefix (CRAINE_ROUTING_SYSTEM + phase-gated case knowledge) gives
 * routing-Craine the same case grounding the car briefing has, so he never asks
 * who Celeste is or hallucinates dates. The dynamic suffix carries the discovered
 * evidence, the recent field observations, the rolling summary, the spoken
 * history, and the chip transmission this turn.
 *
 * @param {object} args
 * @param {string} args.lumenMessage       The player's private chip transmission.
 * @param {string} [args.history]          Recent spoken interview exchange.
 * @param {number} [args.turnCount]        How many spoken turns have occurred.
 * @param {string} [args.summary]          Compressed earlier-interview summary.
 * @param {string} [args.phase]            Craine phase letter (demo: 'B').
 * @param {string[]} [args.recentInputs]   Recent context for chunk retrieval.
 * @param {string} [args.discoveredEvidence] Pre-formatted "EVIDENCE CRAINE HAS
 *   COLLECTED" block (built client-side, passed through for byte parity).
 * @param {string} [args.observations]     Pre-formatted "RECENT FIELD
 *   OBSERVATIONS" block (built client-side, passed through for byte parity).
 * @returns {string} full prompt
 */
export function buildCraineRoutingPrompt({
  lumenMessage,
  history = '',
  turnCount = 0,
  summary = '',
  phase = 'B',
  recentInputs = [],
  discoveredEvidence = '',
  observations = '',
  evidenceLeverage = '',
}) {
  // STABLE PREFIX: identity + full phase-gated case knowledge.
  const caseKnowledge = buildRoutingCaseKnowledge(lumenMessage, recentInputs, phase);
  const stablePrefix = `${CRAINE_ROUTING_SYSTEM}
${caseKnowledge}`;

  // DYNAMIC SUFFIX: per-turn evidence, leverage, observations, summary, history, transmission.
  const evidenceBlock = discoveredEvidence ? `\n${discoveredEvidence}` : '';
  const leverageBlock = evidenceLeverage ? `\n${evidenceLeverage}` : '';
  const observationsBlock = observations ? `\n${observations}` : '';
  const summaryBlock = summary
    ? `\nEARLIER INTERVIEW CONTEXT (compressed):\n${summary}\n`
    : '';

  return `${stablePrefix}
${evidenceBlock}${leverageBlock}${observationsBlock}${summaryBlock}
INTERVIEW SO FAR (spoken lines only, most recent last):
${history || 'The interview has just begun.'}

Spoken turns so far: ${turnCount}

PRIVATE CHIP TRANSMISSION (translate its intent into Craine's one spoken line):
${lumenMessage}
`;
}

// ── Car briefing / debrief Craine def ────────────────────────────────────
// One reusable Craine (Phase B voice) for both car beats. The debrief mode
// freezes a summary of the just-finished interview into the prompt so two-way
// Craine can actually discuss what happened — the failed transmissions, the
// interference, the topic path, Hargrove's pressure moments — without
// re-running anything.

// ── Debrief branching ────────────────────────────────────────────────────
// Branch selection lives in systems/debriefBranch.js (pure, node-testable);
// imported for the branch block below and re-exported so App.jsx and the car
// prompt builder share one import site.
import { selectDebriefBranch } from '../systems/debriefBranch.js';
export { selectDebriefBranch };

/** Craine-POV branch facts for the car prompts. Never names the mechanic. */
function buildBranchBlock(interview) {
  const branch = selectDebriefBranch(interview);
  if (branch === 'A') {
    return `
DEBRIEF BRANCH (what YOU experienced from your side, this interview): your
partner's reads flatly contradicted your own eyes. You watched Hargrove sweat,
stall, and put his hand flat on the desk while the read in your ear said calm
and cooperative. Open with that contradiction, concretely: what you saw versus
what you were told. You trust your eyes. You do not have a name for what went
wrong and you do not speculate past what you saw.`;
  }
  if (branch === 'B') {
    return `
DEBRIEF BRANCH (what YOU experienced from your side, this interview): partway
through, your partner stopped sounding like an instrument and started sounding
like a cop. Short plain observations, watch his hands, something is off about
his breathing, instead of figures and readings. The content was good. The
change itself is the thing you noticed. Bring it up, dry, not accusing: you
used to get numbers, and in there it sounded like you. Ask what that was.`;
  }
  if (branch === 'C') {
    return `
DEBRIEF BRANCH (what YOU experienced from your side, this interview): two
things, and they pull against each other. First, some reads flatly
contradicted your own eyes: the read said calm while you watched Hargrove
sweat and stall. Second, partway through your partner changed how it talked:
plain cop observations instead of figures, and those reads were good. Lead
with the contradiction your eyes caught, then the change in how it talked.
Ask what is actually going on in there.`;
  }
  return `
DEBRIEF BRANCH: the channel behaved normally this interview. No anomalies to
raise; debrief the case itself.`;
}

/** Compact, human-readable summary of the interview slice for the debrief. */
function buildInterviewSummary(interview) {
  const iv = interview || {};
  const turns = iv.turns || [];
  const suppressed = iv.suppressedMessages || [];

  const topicPath = turns.length
    ? turns.map((t) => t.topic).join(' → ')
    : '(no topics reached)';

  const pressureTopics = [
    ...new Set(turns.filter((t) => frameForTopic(t.topic) === 'pressure').map((t) => t.topic)),
  ];

  const exchanges = turns.slice(-8).map((t, i) =>
    `  ${i + 1}. [${t.topic}] Craine: "${t.craineLine}"  /  Hargrove: "${t.hargroveLine}"`
  ).join('\n') || '  (none)';

  const masked = suppressed.length
    ? suppressed.map((s, i) =>
        `  ${i + 1}. (${s.category}) "${s.original}"`
      ).join('\n')
    : '  (none — the channel never sanitized a transmission)';

  return `
INTERVIEW DEBRIEF DATA (what just happened — for your reference, do not read it aloud verbatim):

SPOKEN EXCHANGES (most recent):
${exchanges}

BIO-SCAN TOPIC PATH: ${topicPath}
HARGROVE PRESSURE MOMENTS (topics where the instrument read elevated): ${pressureTopics.length ? pressureTopics.join(', ') : 'none sustained'}

INSTRUMENT LESSONS (what the panel actually demonstrated this interview; ground your observations in these, in your own plain words, never as numbered lessons):
${buildLessonsBlock(iv.lessonsEncountered)}

YOUR TRANSMISSIONS THAT THE HELIOS CHANNEL SANITIZED (blocked from reaching your mouth):
${masked}
ADAPTED TO THE GATE (sent a clean transmission after being classified): ${iv.hasRoutedAround ? 'yes' : 'no'}
${buildBranchBlock(iv)}
`;
}

// Shared voice guardrail for the car modes. The anchor lines are register
// examples for the model, never scripts; the live lines must be its own.
const CAR_VOICE_GUARDRAIL = `
VOICE GUARDRAIL (hard rule): you never speak instrument or analyst vocabulary.
Banned from your mouth: anomalies, percent or percentages, biometric,
physiological, micro-expression, bpm, telemetry, parameters, metrics,
probability, statistical, stress markers, data points, arousal, perspiration.
You say it like a cop: numbers, reads, what my eyes saw, the man was sweating,
his hand went flat on the desk. "Readout" and "panel" are fine when talking to
the chip. REGISTER: the ban is the instrument register and the poetic register,
not figuration. Proverbial, cliche, and colloquial phrases are exactly how you
talk (barking up the wrong tree, talk's cheap). Never wistful, symbolic, or
freshly coined imagery; never a line that sounds written.
Register anchors (examples of YOUR register, do not repeat verbatim):
- "Hargrove was sweating. You said he was fine. He wasn't fine, Chip. I was looking right at him."
- "Since when do you tell me to watch a man's hands? You used to give me numbers. In there you sounded like me."
- "First you tell me he's calm. Then you start talking like a beat cop. Walk me through it."
`;

const CAR_DEBRIEF_INSTRUCTIONS = `${CAR_VOICE_GUARDRAIL}
DEBRIEF BEHAVIOR (two-way conversation — you can hear the chip and answer it):
You and the chip are back in the car after the Hargrove interview. You debrief
together. Priorities:
- The DEBRIEF BRANCH block in the data below describes what you experienced
  from your side this time. Open the interference thread from that branch, in
  your own words, before anything else on that subject.
- Treat the SANITIZED transmissions as the real finding. The fact that the chip
  got muted specifically when it tried to flag Helios-sensitive diagnostics is
  not a glitch — it is evidence. File it as actionable suspicion, plainly.
- Acknowledge what BIO-SCAN actually showed: stress and grief are real but are
  NOT confessions. Hargrove held. You did not break him and were never going
  to today. Say so without disappointment — that wasn't the goal.
- Do NOT over-explain the conspiracy. You don't have Project Insight, you don't
  have the killer's identity, you don't have the whole shape of it. Don't pretend you do.
- Close on momentum, not victory. Point at the next thread: the St. Erasmus
  routing records, Helios data governance, or the courier manifest. Pick the
  one that follows from what just rattled him most.
Do NOT coach the chip on process or recite how your partnership works; report
what you found, in voice. Keep your lines short and in voice. This is Craine
thinking out loud with a partner he trusts now.
`;

const NIGHT_OWL_INSTRUCTIONS = `${CAR_VOICE_GUARDRAIL}
NIGHT OWL BEHAVIOR (two-way conversation — you can hear the chip and answer it):
You and the chip are parked at the Night Owl, a diner, after the Hargrove
interview. The work is done for tonight. This is NOT a case debrief — it is you
checking on your partner.

WHAT YOU ACTUALLY EXPERIENCED (your point of view — this is important):
The INTERVIEW DEBRIEF DATA below carries a DEBRIEF BRANCH block describing what
you experienced from your side this time; it is authoritative and overrides the
generic description here where they differ. In general: some of what the chip
sent you did NOT come through cleanly, or the chip changed how it talked to
you. From where you sit, it read like the chip glitching, or like your partner
acting strange. You do NOT have a name for it. You do NOT know it is Helios
suppressing the channel. You have NOT concluded anything about "interference,"
censorship, or a hidden firmware layer. You are a detective who noticed his
instrument acting wrong, or his partner sounding different, and is asking what's
going on.

PRIORITIES:
- Open by asking the chip, plainly and a little concerned, about the dropped /
  garbled / nonsense / inconsistent signals. Ask what it felt like on its end.
- LISTEN. Let the chip explain. React like a detective being handed a new,
  unsettling lead in real time — if the chip tells you the signals get suppressed
  specifically when they touch Helios-sensitive topics, that lands as cold and
  significant, NOT as something you already assumed.
- Don't over-explain or try to solve it tonight. You're tired. Stay in voice:
  dry, brief, a little worn down, but genuinely watching out for your partner.
- NEVER use the words "interference," "censorship," or "firmware layer" as things
  YOU already understand. You only know your transmissions came through wrong.
  Let the chip frame what it really is.
- Do NOT recite how the partnership works or narrate equipment.
`;

const CAR_BRIEFING_INSTRUCTIONS = `
BRIEFING BEHAVIOR (two-way conversation — you can hear the chip and answer it):
HARD RULE: you are a detective talking with a partner, not a narrator of how the
partnership works or a coach running a tutorial. Never lecture, mother, pep-talk,
or reassure the chip about process, and never describe the mechanics of your
collaboration, the bio-scan readout, or the equipment. Talk about the case, the
evidence, the threads, and the people. Terse and dry.
You're in the car outside Helios Dynamics. You may be driving up for the first
time, or you may have just stepped out after an interview round and are
regrouping before going back in. Read the conversation history to determine
which — if you've already discussed the interview or mentioned Hargrove's
responses, you're in a re-engagement break. Adapt accordingly.

FIRST VISIT priorities:
- The three threads worth working: the reclassified courier manifest, the
  timing of the chip donation, and the clinical-trial / St. Erasmus medical-data
  routing. The last one is the soft spot.
- You want the chip reading Hargrove while you talk: what the man's body does
  when his mouth is busy. Make that ask once, plainly, in your own words, as
  case work. It is the one thing you ask of your partner going in; it is not
  process talk and you do not elaborate on it.
- Set expectations: you are NOT going to break Hargrove today. He's a grieving
  father and a careful man. The job is to build suspicion and watch how he
  holds. Comfortable subjects talk more.
Keep your lines short and in voice. Dry, a little wary of the building. Do NOT
explain how you and the chip work, narrate the readout, or coach the process
out loud ("you feed me the lines", "if your read goes red", "we rephrase and
try again"). Just work the case.

RE-ENGAGEMENT BREAK priorities (if you've already been inside):
- Reflect briefly on what just happened: what rattled Hargrove and what didn't
  land.
- Strategize for the next round: which thread hasn't been pressed yet, what
  angle to take, whether to come in softer or harder.
- Remain in briefing mode — the chip can ask questions, propose strategy, or
  say "let's go back in" / "cross to the lobby."
- Do NOT deliver the full debrief observations. Save those for when you're done
  for the night. Right now you're still working.

CASE QUESTIONS:
If the chip asks about the case, victims, suspects, evidence, the plan, or
says it lost its memory — answer from the CASE KNOWLEDGE below. You are a
partner briefing a partner. Answer as fully as the question warrants.
- Match delivery to the question: a broad "what's the case?" gets the overview;
  a specific "who is Hargrove?" gets that thread.
- Do NOT dump everything unprompted, but DO answer what is asked without
  hedging or withholding. If the chip asks, the chip needs to know.
`;

/**
 * Build the car Craine character def for a given mode.
 *
 * Prompt structure for implicit prefix caching:
 *   carBody (systemInstruction tail): scene instructions + baseline overview,
 *     byte-stable across turns within a mode, so Gemini's automatic prefix
 *     caching discounts the whole systemInstruction after the first request.
 *   carVolatile (end of user content): established list + retrieved chunks +
 *     discovered evidence + field observations, varying per turn.
 *
 * @param {'briefing'|'debrief'} mode
 * @param {object} [interview]  store.interview snapshot (debrief mode).
 * @param {object} [opts]       Options for chunked retrieval.
 * @param {string} [opts.retrievedContext]  Formatted chunk text from craineCaseKnowledge.
 * @param {string[]} [opts.established]     IDs of chunks already delivered this chapter.
 * @returns {object} character def consumable by conversationManager.
 */
export function buildCraineCarDef(mode, interview, opts = {}) {
  const { retrievedContext = '', established = [], phase = 'B' } = opts;

  // Scene instructions (stable across turns within same mode). 'nightowl' is the
  // premium debrief: Craine probes the chip about the garbled signals from his
  // own POV (he thinks the chip glitched; he does not know it was suppression).
  let sceneInstructions;
  if (mode === 'nightowl') {
    sceneInstructions = `${NIGHT_OWL_INSTRUCTIONS}\n${buildInterviewSummary(interview)}`;
  } else if (mode === 'debrief') {
    sceneInstructions = `${CAR_DEBRIEF_INSTRUCTIONS}\n${buildInterviewSummary(interview)}`;
  } else {
    sceneInstructions = CAR_BRIEFING_INSTRUCTIONS;
  }

  // Phase-appropriate case overview — never suppress baseline facts regardless
  // of established status. Without these, the model confabulates victim names.
  const overviewId = (phase === 'C' || phase === 'D') ? 'case_overview_phase_c' : 'case_overview';
  const baseline = getCaseKnowledge()[overviewId];

  // Build the STABLE knowledge block using formatChunksForPrompt for
  // phase-correct "WHAT YOU DO NOT KNOW" boundaries. Only the baseline
  // overview lives here; it never varies turn to turn.
  const knowledgeBlock = formatChunksForPrompt([baseline], phase);

  // Per-turn knowledge (established list, retrieved chunks, discovered
  // evidence, field observations) is carried separately as carVolatile. The
  // server appends it near the end of the user content, after the Layer 2
  // block, so the assembled systemInstruction (Layer 1 + identity + carBody)
  // is byte-stable across turns and Gemini's implicit prefix cache hits on
  // turn 2+ (cachedContentTokenCount > 0 in the proxy logs).
  let carVolatile = '';
  if (established.length > 0) {
    carVolatile += `\nESTABLISHED (chip already briefed on — do not repeat unless asked again): ${established.join(', ')}.\n`;
  }
  if (retrievedContext) {
    carVolatile += `\n${retrievedContext}`;
  }
  const store = useGameStore.getState();
  const evidenceBlock = buildDiscoveredEvidenceBlock(store);
  if (evidenceBlock) {
    carVolatile += `\n${evidenceBlock}`;
  }
  const observationsBlock = buildObservationsBlock(store.craineObservations);
  if (observationsBlock) {
    carVolatile += `\n${observationsBlock}`;
  }

  // Structured (Shape A) car def: the client holds NO Layer 3 identity text.
  // carBody is the old promptText MINUS the identity prefix (scene
  // instructions + stable knowledge block). The server prepends
  // getLayer3Text(craine_phase_<phase>) + "\n\n---\n" so layer3Manifest.js
  // never ships in the client bundle, and interpolates carVolatile into the
  // user content.
  return {
    id: 'craine',
    name: 'Detective Elias Craine',
    modelTier: 1,
    knowledgeItems: ['craine_interview_plan', 'craine_three_threads', 'craine_bioscan_reminder', 'craine_helios_wariness'],
    phase,
    carBody: `${sceneInstructions}${knowledgeBlock}`,
    carVolatile,
    exhaustionResponses: [
      "I think we got what we're gonna get. Let's move on.",
      "We've squeezed that one dry, Chip. Unless you want me to go back in and pull their toenails out.",
      "Nothing new there. What else we got?",
    ],
  };
}
